import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ArrowUpRight, TrendingUp, Play, X } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow, EASE } from "@/components/motion";

const VIDEO_TESTIMONIALS = [
  { src: "/videos/testimonial-1.mp4", poster: "/videos/poster-1.jpg", name: "Joey R.", plan: "Diamond Package" },
  { src: "/videos/testimonial-2.mp4", poster: "/videos/poster-2.jpg", name: "Omiania G.", plan: "Diamond Package" },
  { src: "/videos/testimonial-3.mp4", poster: "/videos/poster-3.jpg", name: "Abdul A.", plan: "Basic Annual Package" },
];

const FEATURED = {
  name: "Alicia R.",
  city: "Washington, DC",
  plan: "Diamond",
  result: "$75K FUNDED",
  quote: "I came to Clear Path with a 590 and a drawer full of denial letters. Eight months later my score crossed 720 and the funding team matched me with $75,000 for my catering business. Nobody else even mentioned funding. They rebuilt my credit AND my plans.",
};

const TESTIMONIALS = [
  { name: "Dana M.", city: "Atlanta, GA", plan: "Prestigious", result: "+169 PTS",
    quote: "169 points in five months. But honestly, the score isn't even the best part. They taught me how to keep it up. I'll never be back in that hole again." },
  { name: "Marcus T.", city: "Houston, TX", plan: "Prestigious", result: "4.9% AUTO RATE",
    quote: "Went from getting denied everywhere to driving off the lot at 4.9%. The Metro 2 audit stuff sounded like jargon until the deletions started hitting my inbox." },
  { name: "Priya S.", city: "Phoenix, AZ", plan: "Diamond", result: "+167 PTS",
    quote: "A repossession I thought would haunt me for seven years? Gone in month three. My architect called me every single month. That never happens in this industry." },
  { name: "Jerome W.", city: "Tampa, FL", plan: "Skool Community", result: "SCORE 601 → 704",
    quote: "The weekly live calls alone are worth ten times the price. Real questions, real answers, and a community that actually celebrates your wins." },
  { name: "Devon K.", city: "Orlando, FL", plan: "Basic (Annual)", result: "9 DELETIONS",
    quote: "Nine collections deleted in forty days. I watched every single one fall off. Wish I'd done this two years ago instead of paying that other company to do nothing." },
  { name: "Tanya B.", city: "Charlotte, NC", plan: "Diamond", result: "HOME LOAN APPROVED",
    quote: "Approved for our first home in June. My lender asked what changed. I told her: Clear Path happened. Best money I've ever spent, period." },
];

function Stars() {
  return (
    <div className="flex gap-1 mb-5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="w-4 h-4 text-gold fill-gold" />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <div data-testid="testimonials-page">
      {/* Hero */}
      <section className="py-28 md:py-36 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(93,123,255,0.07),transparent_55%)]" />
        <motion.div
          className="absolute w-[380px] h-[380px] rounded-full bg-[#8B45F0]/12 blur-[110px] -bottom-20 -left-20 pointer-events-none"
          animate={{ y: [0, 30, 0], x: [0, 20, 0] }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="max-w-7xl mx-auto relative">
          <Eyebrow className="mb-6">Client Stories</Eyebrow>
          <MaskedLines
            lines={["Real people.", "Real deletions.", "Real comebacks."]}
            className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.08] text-white"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
            className="mt-8 text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl"
          >
            Every story below is from a client who sat exactly where you're sitting now.
            Shared with their permission.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="mt-10 flex flex-wrap gap-8"
          >
            {[["4.9/5", "Average Rating"], ["4,200+", "Files Restored"], ["93%", "First-Cycle Deletions"]].map(([v, l]) => (
              <div key={l}>
                <p className="font-serif text-2xl md:text-3xl text-white">{v}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Video testimonials */}
      <section data-testid="video-testimonials-section" className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5 bg-surface/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(139,69,240,0.06),transparent_55%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <Reveal>
            <Eyebrow className="mb-6">Watch Their Stories</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mb-4">
              Hear it <span className="italic text-gold">in their own words.</span>
            </h2>
            <p className="text-slate-400 text-sm md:text-base leading-relaxed max-w-xl mb-14">
              Unscripted, unedited — clients sharing what changed after Clear Path.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {VIDEO_TESTIMONIALS.map((v, i) => (
              <Reveal key={v.src} delay={(i % 3) * 0.12}>
                <button
                  data-testid={`video-card-${i}`}
                  onClick={() => setActiveVideo(i)}
                  className="group relative w-full bg-[#101218] border border-white/10 rounded-2xl overflow-hidden hover:border-gold/40 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.37)] text-left"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={v.poster}
                      alt={`${v.name} video testimonial`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-obsidian/20 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span
                        data-testid={`video-play-${i}`}
                        className="w-16 h-16 rounded-full bg-gold/90 text-obsidian flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:bg-gold shadow-[0_0_32px_rgba(93,123,255,0.35)]"
                      >
                        <Play className="w-6 h-6 fill-current ml-1" />
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="font-serif text-lg text-white">{v.name}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-gold mt-1">{v.plan}</p>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured quote */}
      <section className="py-20 px-6 lg:px-12 border-t border-white/5 bg-surface/30">
        <Reveal className="max-w-5xl mx-auto">
          <div data-testid="featured-testimonial" className="bg-[#101218] border border-gold/25 rounded-3xl p-10 md:p-16 relative overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(93,123,255,0.08),transparent_60%)]" />
            <div className="relative">
              <Quote className="w-10 h-10 text-gold/60 mb-8" />
              <p className="font-serif text-xl md:text-3xl text-white leading-snug mb-10">{FEATURED.quote}</p>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-serif text-xl text-white">{FEATURED.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                    {FEATURED.city} · {FEATURED.plan}
                  </p>
                </div>
                <span className="bg-emerald/10 text-emerald border border-emerald/30 px-4 py-2 rounded-full text-xs font-mono tracking-widest">
                  {FEATURED.result}
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Grid */}
      <section className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={(i % 3) * 0.1}>
              <div data-testid={`testimonial-card-${i}`}
                className="h-full bg-[#101218] border border-white/10 rounded-2xl p-8 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.37)] flex flex-col">
                <Stars />
                <p className="text-slate-300 text-sm leading-relaxed flex-1 mb-8">"{t.quote}"</p>
                <div className="flex items-center justify-between border-t border-white/5 pt-5">
                  <div>
                    <p className="font-serif text-base text-white">{t.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">
                      {t.city} · {t.plan}
                    </p>
                  </div>
                  <span className="bg-gold/10 text-gold border border-gold/30 px-3 py-1.5 rounded-full text-[10px] font-mono tracking-widest whitespace-nowrap">
                    {t.result}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-12 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(93,123,255,0.07),transparent_60%)]" />
        <Reveal className="max-w-3xl mx-auto text-center relative">
          <TrendingUp className="w-8 h-8 text-emerald mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mb-6">
            Your comeback story <span className="italic text-gold">starts with one call.</span>
          </h2>
          <p className="text-slate-400 leading-relaxed mb-10">
            Free 15-minute audit call. We'll tell you exactly what's holding your score down and how fast it can move.
          </p>
          <Link to="/contact" data-testid="testimonials-cta-book-call"
            className="inline-flex items-center gap-2 bg-gold text-obsidian hover:bg-gold-light hover:scale-[1.02] active:scale-[0.98] font-mono text-xs uppercase tracking-[0.15em] px-10 py-4 rounded-xl transition-all duration-200 shadow-[0_0_24px_rgba(93,123,255,0.3)]">
            Book My Free Call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      {/* Video lightbox */}
      <AnimatePresence>
        {activeVideo !== null && (
          <motion.div
            data-testid="video-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-obsidian/90 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            onClick={() => setActiveVideo(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 20 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="relative w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="video-modal-close"
                onClick={() => setActiveVideo(null)}
                className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-gold hover:text-obsidian text-white flex items-center justify-center transition-colors duration-200"
                aria-label="Close video"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="rounded-2xl overflow-hidden border border-gold/25 shadow-[0_24px_64px_rgba(0,0,0,0.6)] bg-black">
                <video
                  data-testid="video-modal-player"
                  src={VIDEO_TESTIMONIALS[activeVideo].src}
                  poster={VIDEO_TESTIMONIALS[activeVideo].poster}
                  controls
                  autoPlay
                  playsInline
                  className="w-full max-h-[75vh] object-contain"
                />
              </div>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {VIDEO_TESTIMONIALS[activeVideo].name} · {VIDEO_TESTIMONIALS[activeVideo].plan}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
